import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatDate, formatFreightValueDisplay } from "../mockData";
import { formatCarrierQueueWaitingTime } from "../availableFreight.mapper";
import { isPriceVisibilityMasked, isAnonymousShipperLabel, getAnonymousShipperBannerText } from "../../../util/freight/carrierPriceVisibility";

function DetailRow({ label, value }) {
  return (
    <Box className="lances-detail-row">
      <Typography variant="body2" className="lances-detail-row__label">
        {label}
      </Typography>
      <Typography variant="body2" className="lances-detail-row__value">
        {value}
      </Typography>
    </Box>
  );
}

export default function FreightDetailPanel({
  freight,
  loading,
  error,
  acceptingFreightId,
  onClose,
  onAcceptFreight,
}) {
  if (loading) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <CircularProgress size={32} sx={{ color: "#012443" }} />
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Carregando detalhes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Button variant="text" onClick={onClose} sx={{ mt: 2 }}>
          Fechar
        </Button>
      </Box>
    );
  }

  if (!freight) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <Typography color="text.secondary" align="center">
          Selecione um frete para ver os detalhes
        </Typography>
      </Box>
    );
  }

  const isAccepting = acceptingFreightId === freight.id;
  const isInQueue = Boolean(freight.isInCarrierQueue);
  const isMasked = isPriceVisibilityMasked(freight);
  const badgeLabel = freight.modality ?? "PREÇO FIXO";
  const queueWaitingTime = isInQueue ? formatCarrierQueueWaitingTime(freight) : null;
  const shipperName =
    freight.priceVisibility?.displayShipperName || freight.shipper;
  const isAnonymousShipper = isAnonymousShipperLabel(shipperName);
  const displayPrice =
    freight.priceVisibility?.displayPrice ?? freight.freightValue;

  return (
    <Box
      className={[
        "lances-detail-panel",
        isMasked ? "lances-detail-panel--external" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Box className="lances-detail-panel__header">
        <Typography variant="h6" fontWeight={700}>
          Detalhes do Frete
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="lances-detail-panel__content">
        <Box className="lances-detail-panel__summary">
          {isInQueue ? (
            <Box className="lances-freight-card__candidate-strip lances-freight-card__candidate-strip--detail">
              Candidato a este frete
            </Box>
          ) : null}
          {isMasked ? (
            <Box className="lances-freight-card__external-strip lances-freight-card__external-strip--detail">
              Valor Anunciado — somente visualização
            </Box>
          ) : null}
          {isAnonymousShipper ? (
            <Box className="lances-freight-card__anonymous-strip lances-freight-card__anonymous-strip--detail">
              {getAnonymousShipperBannerText(shipperName)}
            </Box>
          ) : null}
          <span className="lances-badge lances-badge--fixed">
            {badgeLabel}
          </span>
          <Typography fontWeight={700} className="lances-detail-panel__number">
            {freight.number}
          </Typography>
          <Box className="lances-detail-panel__route">
            <FmdGoodOutlinedIcon fontSize="small" className="lances-icon-muted" />
            <Typography variant="body2" fontWeight={600}>
              {freight.origin}
            </Typography>
            <ArrowForwardIcon fontSize="small" className="lances-icon-muted" />
            <FmdGoodOutlinedIcon fontSize="small" className="lances-icon-muted" />
            <Typography variant="body2" fontWeight={600}>
              {freight.destination}
            </Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" className="lances-detail-section-title">
          Dados do Frete
        </Typography>
        <Box className="lances-detail-section">
          <DetailRow
            label="Embarcador"
            value={
              isAnonymousShipper
                ? getAnonymousShipperBannerText(shipperName)
                : shipperName
            }
          />
          <DetailRow label="Tipo de Veículo" value={freight.vehicleType} />
          <DetailRow label="Tipo de Carroceria" value={freight.bodyType} />
          <DetailRow label="Peso" value={freight.weight} />
          <DetailRow label="Cubagem" value={freight.volume} />
          <DetailRow label="Data da Coleta" value={formatDate(freight.collectionDate)} />
          <DetailRow label="Data de Entrega" value={formatDate(freight.deliveryDate)} />
          <DetailRow label="Modalidade" value={freight.modality} />
        </Box>

        {freight.observations?.length > 0 && (
          <>
            <Typography variant="subtitle2" className="lances-detail-section-title">
              Observações
            </Typography>
            <ul className="lances-observations">
              {freight.observations.map((obs) => (
                <li key={obs}>
                  <Typography variant="body2">{obs}</Typography>
                </li>
              ))}
            </ul>
          </>
        )}

        <Typography variant="subtitle2" className="lances-detail-section-title">
          {isMasked ? "Valor Anunciado" : "Valor do Frete"}
        </Typography>
        <Typography className="lances-bid-dispute__value">
          {formatFreightValueDisplay(displayPrice, freight.pricingType)}
        </Typography>
        {isInQueue && (
          <Box className="lances-freight-card__queue-hint-wrap" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Você está na fila aguardando contratação
            </Typography>
            {queueWaitingTime ? (
              <Typography variant="body2" color="text.secondary">
                há {queueWaitingTime}
              </Typography>
            ) : null}
          </Box>
        )}
        <Button
          variant="contained"
          fullWidth
          className={isInQueue ? "lances-btn-leave-queue" : "lances-btn-accept"}
          sx={{ mt: 2 }}
          disabled={isAccepting || (isMasked && !isInQueue)}
          onClick={() => {
            if (isMasked && !isInQueue) return;
            onAcceptFreight(freight);
          }}
        >
          {isAccepting ? (
            <CircularProgress
              size={20}
              sx={{ color: isInQueue ? "#15803d" : "#fff" }}
            />
          ) : isInQueue ? (
            "Sair da fila"
          ) : isMasked ? (
            "Somente visualização"
          ) : (
            "Entrar na fila"
          )}
        </Button>
      </Box>
    </Box>
  );
}
